import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import { Box, Button, Container, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageHeader from '~/components/Common/Elements/PageHeader';

import useAppNavigation from '~/hooks/useAppNavigation';

import { diffDays, formatDateShort, isAfter, isBefore } from '~/services/TimeService';
import * as toursService from '~/services/ToursService';

import useTourDealsPage from './useTourDealsPage';

export interface TourDealStatus {
  status: string;
  color: string;
}

function getTourDealStatus(startDate: string, endDate: string): TourDealStatus | undefined {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isBefore(now, start)) {
    const daysUntilStart = diffDays(start, now, 'day');
    const daysUntilStartString = daysUntilStart === 1 ? 'day' : 'days';
    return {
      status: `live in ${daysUntilStart} ${daysUntilStartString}`,
      color: 'green',
    };
  }

  if (isAfter(now, end)) {
    const daysSinceEnd = diffDays(now, end, 'day');
    const daysSinceEndString = daysSinceEnd === 1 ? 'day' : 'days';
    return {
      status: `ended ${daysSinceEnd} ${daysSinceEndString} ago`,
      color: 'red',
    };
  }

  if (isBefore(start, end)) {
    return {
      status: 'currently live',
      color: 'green',
    };
  }

  return;
}

export default function TourDealsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteButton, setDeleteButton] = useState(null);
  const { dealsPage, page, total, recordsPerPage, paginate, getDealsPage, isLoading } = useTourDealsPage({});
  const { push } = useAppNavigation();

  const handleDeleteDeal = useCallback(
    async (id: string) => {
      try {
        await toursService.deleteTourDealById(id);
        enqueueSnackbar('Deal Page deleted', { variant: 'success' });
      } catch (error) {
        if (error.status === 404) {
          enqueueSnackbar('This deal page is already deleted', { variant: 'error' });
        } else {
          enqueueSnackbar('Error deleting Deal Page', { variant: 'error' });
        }
      }
    },
    [enqueueSnackbar],
  );

  const actionColumn = useCallback(
    (cell, row) => (
      <Box>
        {deleteButton !== row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              href={`${window.configs.LUX_CUSTOMER_PORTAL}/tours/deal/${row.slug || row.id}`}
              target="_blank"
              rel="noreferrer"
            >
              Preview
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                push(`/tours/deals/${row.id}/edit`);
              }}
            >
              Edit
            </Button>

            <Button
              variant="text"
              size="small"
              color="error"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(row.id);
              }}
            >
              Delete
            </Button>
          </Box>
        )}

        {deleteButton === row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await handleDeleteDeal(row.id);
                getDealsPage();
              }}
            >
              Confirm
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    ),
    [deleteButton, handleDeleteDeal, getDealsPage, push],
  );

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Deal Page Name',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      display: 'flex',
    },
    {
      field: 'numTours',
      headerName: 'Tours',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => params.row.tours?.length || 0,
      display: 'flex',
    },
    {
      field: 'startAt',
      headerName: 'Start Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => formatDateShort(params.value),
      display: 'flex',
    },
    {
      field: 'endAt',
      headerName: 'End Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => formatDateShort(params.value),
      display: 'flex',
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      flex: 2,
      renderCell: (params) => {
        const status = getTourDealStatus(params.row.startAt, params.row.endAt);
        return status?.status;
      },
      display: 'flex',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 230,
      sortable: false,
      renderCell: (params) => actionColumn(params.value, params.row),
      display: 'flex',
    },
  ];

  return (
    <Container>
      <PageHeader title="Tour Deals" />
      <Box mt={1}>
        <Stack mt={3} width="100%" direction="row" justifyContent="flex-end" spacing={2}>
          <Button component={Link} to="/tours/deals/create" variant="outlined" size="small">
            Add New Deal Page
          </Button>
        </Stack>
        <DataGrid
          loading={isLoading}
          rows={dealsPage || []}
          columns={columns}
          pagination
          paginationModel={{ page: page - 1, pageSize: recordsPerPage }}
          pageSizeOptions={[10]}
          paginationMode="server"
          rowCount={total}
          onPaginationModelChange={({ page }) => paginate(page + 1)}
          slots={{ pagination: GridPagination }}
          autoHeight
        />
      </Box>
    </Container>
  );
}
